import {
  type Dispatch,
  forwardRef,
  type HTMLAttributes,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { CALENDAR_BRAND_TEAL } from '@/components/ui/calendar.styles';
import { i18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

import 'dayjs/locale/de';
import 'dayjs/locale/en';

dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);

export interface DatePickerProps
  extends Omit<HTMLAttributes<HTMLButtonElement>, 'value' | 'onChange'> {
  value?: Date;
  name?: string;
  onChange: (event: { target: { value: Date | null; name?: string } }) => void;
  setIsValid: Dispatch<SetStateAction<boolean>>;
  isValid: boolean;
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  ({ className, value, name, onChange, setIsValid, isValid, ...buttonProps }, ref) => {
    const { t } = useTranslation();

    const currentLocale = i18n.language;

    useEffect(() => {
      dayjs.locale(currentLocale);
    }, [currentLocale]);

    const localeFormat = currentLocale === 'de' ? 'DD.MM.YYYY' : 'MM.DD.YYYY';

    const formatDate = (date: Date | null) => (date ? dayjs(date).format(localeFormat) : '');
    const parseDate = (dateString: string) => {
      const parsed = dayjs(dateString, localeFormat);
      return parsed.isValid() ? parsed.toDate() : null;
    };

    const [manualInput, setManualInput] = useState<string>(formatDate(value || null));

    // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
    useEffect(() => {
      setManualInput(formatDate(value || null));
    }, [value]);

    const onChangeHandler = (date?: Date) => {
      setIsValid(true);
      onChange({
        target: {
          name,
          value: date || null,
        },
      });
      setManualInput(formatDate(date || null));
    };

    const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputDate = e.target.value;
      setManualInput(inputDate);

      const regex =
        currentLocale === 'de'
          ? /^([012][0-9]|3[01])\.(0[1-9]|1[012])\.\d{4}$/
          : /^(0[1-9]|1[012])\.([012][0-9]|3[01])\.\d{4}$/;

      setIsValid(inputDate.trim() === '' || regex.test(inputDate));

      if (regex.test(inputDate)) {
        const parsedDate = parseDate(inputDate);
        if (parsedDate) {
          onChangeHandler(parsedDate);
        }
      } else if (inputDate.trim() === '') {
        onChangeHandler(undefined);
      }
    };

    return (
      <Popover>
        <PopoverTrigger
          ref={ref}
          className={cn('rounded-md border px-4 py-2', className)}
          {...buttonProps}
          asChild
        >
          <Button
            variant="outline"
            className={cn(
              'w-[240px] flex-col justify-start text-left font-normal',
              !isValid && 'text-muted-foreground'
            )}
          >
            <input
              type="text"
              value={manualInput}
              onChange={handleManualInputChange}
              placeholder={t('date-type')}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 rounded-xl bg-white p-4 shadow-lg" align="start">
          <Calendar
            className="w-full"
            presetClassNames={CALENDAR_BRAND_TEAL}
            mode="single"
            selected={value}
            onSelect={onChangeHandler}
            defaultMonth={value ?? undefined}
            modifiers={{
              past: { before: new Date(new Date().setHours(0, 0, 0, 0)) },
            }}
            modifiersClassNames={{
              past: '!text-gray-300',
            }}
          />
        </PopoverContent>
      </Popover>
    );
  }
);
